import { useState } from "react";
import { voteAPI } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ScrollArea } from "./ui/scroll-area";
import { toast } from "sonner";
import { Vote, Eye, EyeOff, Settings, Users, Check } from "lucide-react";

export const VotingSettings = ({ open, onOpenChange, board, onUpdate }) => {
  const [votingEnabled, setVotingEnabled] = useState(board?.voting_enabled || false);
  const [votingMode, setVotingMode] = useState(board?.voting_mode || "full_board");
  const [saving, setSaving] = useState(false);
  const [revealing, setRevealing] = useState(false);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await voteAPI.updateSettings(board.id, {
        voting_enabled: votingEnabled,
        voting_mode: votingMode,
        votes_revealed: board.votes_revealed || false,
      });
      toast.success("Voting settings updated");
      onUpdate?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const revealVotes = async () => {
    if (!window.confirm("Reveal all votes? This will apply majority decisions and cannot be undone.")) return;
    setRevealing(true);
    try {
      const res = await voteAPI.reveal(board.id);
      toast.success(`Votes revealed! ${res.data.split_decisions} split decisions found.`);
      onUpdate?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reveal");
    } finally {
      setRevealing(false);
    }
  };

  const participants = board?.participants || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="voting-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Vote className="w-5 h-5 text-indigo-500" />
            Voting Settings
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium text-slate-700">Enable Voting</Label>
              <p className="text-xs text-slate-400 mt-0.5">Participants vote independently on each stall</p>
            </div>
            <Switch
              data-testid="voting-enabled-toggle"
              checked={votingEnabled}
              onCheckedChange={setVotingEnabled}
            />
          </div>

          {votingEnabled && (
            <>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Voting Scope</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="voting-mode-full"
                    onClick={() => setVotingMode("full_board")}
                    className={`p-3 rounded-xl border-2 text-left transition-colors ${
                      votingMode === "full_board"
                        ? "border-indigo-500 bg-indigo-50"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                  >
                    <Users className={`w-4 h-4 mb-1 ${votingMode === "full_board" ? "text-indigo-600" : "text-slate-400"}`} />
                    <div className={`text-xs font-semibold ${votingMode === "full_board" ? "text-indigo-700" : "text-slate-700"}`}>
                      Full Board
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">Everyone votes on all stalls</div>
                  </button>
                  <button
                    type="button"
                    data-testid="voting-mode-assigned"
                    onClick={() => setVotingMode("assigned")}
                    className={`p-3 rounded-xl border-2 text-left transition-colors ${
                      votingMode === "assigned"
                        ? "border-indigo-500 bg-indigo-50"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                  >
                    <Settings className={`w-4 h-4 mb-1 ${votingMode === "assigned" ? "text-indigo-600" : "text-slate-400"}`} />
                    <div className={`text-xs font-semibold ${votingMode === "assigned" ? "text-indigo-700" : "text-slate-700"}`}>
                      Assigned
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">Assign departments per player</div>
                  </button>
                </div>
              </div>

              <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <EyeOff className="w-4 h-4 text-slate-400" />
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Blind Voting</span>
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Votes are hidden from all participants until you reveal them. After reveal, majority decisions are auto-applied. You can override any decision.
                </p>
              </div>

              {!board?.votes_revealed && (
                <Button
                  data-testid="reveal-votes-btn"
                  onClick={revealVotes}
                  disabled={revealing}
                  variant="outline"
                  className="w-full border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {revealing ? "Revealing..." : "Reveal All Votes"}
                </Button>
              )}

              {board?.votes_revealed && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                  <Check className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
                  <p className="text-sm font-medium text-emerald-800">Votes have been revealed</p>
                  <p className="text-[10px] text-emerald-600">Majority decisions applied. Review results to override.</p>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            data-testid="save-voting-settings-btn"
            onClick={saveSettings}
            disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {saving ? "Saving..." : "Save Settings"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VotingSettings;
